import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, useClerk, SignInButton } from "@clerk/clerk-react";
import {
  ArrowLeft,
  BadgePercent,
  KeyRound,
  Loader2,
  RefreshCw,
  Shield,
  Sparkles,
  Users,
} from "lucide-react";
import "./AdminPage.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const ADMIN_CAPABILITIES = [
  {
    icon: Users,
    title: "User access",
    description: "See who has signed in, trial status, and whether access comes from Stripe, an admin grant, or OSG.",
  },
  {
    icon: Sparkles,
    title: "Grant Pro manually",
    description: "Give someone Pro without checkout — unlimited or for a set number of days — and revoke admin grants later.",
  },
  {
    icon: BadgePercent,
    title: "Stripe promotion codes",
    description: "Create custom coupon codes (percent or amount off). Customers enter them on Stripe Checkout.",
  },
];

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function accessLabel(source) {
  switch (source) {
    case "osg_super_user":
      return "OSG employee";
    case "admin":
      return "Admin grant";
    case "stripe":
      return "Stripe";
    case "trial":
      return "Trial";
    default:
      return source || "—";
  }
}

function discountLabel(coupon) {
  if (coupon.percentOff != null) return `${coupon.percentOff}% off`;
  if (coupon.amountOff != null) {
    return `$${(coupon.amountOff / 100).toFixed(2)} off`;
  }
  return "—";
}

export default function AdminPage({ isSuperUser, isStatusLoading = false }) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const clerk = useClerk();
  const [tab, setTab] = useState("users");
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [userSearch, setUserSearch] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);

  const [coupons, setCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [couponForm, setCouponForm] = useState({
    code: "",
    name: "",
    discountType: "percent",
    percentOff: "20",
    amountOffDollars: "10",
    duration: "once",
    durationInMonths: "3",
    maxRedemptions: "",
    expiresAt: "",
  });
  const [creatingCoupon, setCreatingCoupon] = useState(false);
  const [actionUserId, setActionUserId] = useState(null);

  const apiFetch = useCallback(
    async (path, options = {}) => {
      const token = await getToken();
      const resp = await fetch(`${BACKEND_URL}${path}`, {
        ...options,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...(options.headers || {}),
        },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.error || `Request failed (${resp.status})`);
      }
      return data;
    },
    [getToken]
  );

  const loadUsers = useCallback(
    async (page = 1, search = userSearch) => {
      setUsersLoading(true);
      setErrorMessage("");
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: "50",
        });
        if (search.trim()) params.set("search", search.trim());
        const data = await apiFetch(`/api/v1/admin/users?${params}`);
        setUsers(data.users || []);
        setUsersTotal(data.total || 0);
        setUsersPage(data.page || page);
      } catch (err) {
        setErrorMessage(err.message || "Failed to load users.");
      } finally {
        setUsersLoading(false);
      }
    },
    [apiFetch, userSearch]
  );

  const loadCoupons = useCallback(async () => {
    setCouponsLoading(true);
    setErrorMessage("");
    try {
      const data = await apiFetch("/api/v1/admin/coupons?limit=50");
      setCoupons(data.coupons || []);
    } catch (err) {
      setErrorMessage(err.message || "Failed to load coupons.");
    } finally {
      setCouponsLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !isSuperUser) return;
    if (tab === "users") {
      loadUsers(1);
    } else if (tab === "coupons") {
      loadCoupons();
    }
    // Intentionally reload when tab/auth changes, not on every search keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn, isSuperUser, tab]);

  const handleGrantPro = async (clerkId) => {
    const daysRaw = window.prompt(
      "Grant Pro for how many days? Leave blank for unlimited.",
      ""
    );
    if (daysRaw === null) return;
    const reason = window.prompt("Optional reason for this grant:", "") ?? "";
    let days;
    if (daysRaw.trim()) {
      days = parseInt(daysRaw.trim(), 10);
      if (!Number.isFinite(days) || days < 1) {
        setErrorMessage("Days must be a positive integer.");
        return;
      }
    }
    setActionUserId(clerkId);
    setStatusMessage("");
    setErrorMessage("");
    try {
      await apiFetch(`/api/v1/admin/users/${encodeURIComponent(clerkId)}/grant-pro`, {
        method: "POST",
        body: JSON.stringify({ days: days ?? null, reason }),
      });
      setStatusMessage("Pro access granted.");
      await loadUsers(usersPage);
    } catch (err) {
      setErrorMessage(err.message || "Failed to grant Pro.");
    } finally {
      setActionUserId(null);
    }
  };

  const handleRevokePro = async (clerkId) => {
    if (!window.confirm("Revoke admin-granted Pro for this user?")) return;
    setActionUserId(clerkId);
    setStatusMessage("");
    setErrorMessage("");
    try {
      await apiFetch(`/api/v1/admin/users/${encodeURIComponent(clerkId)}/revoke-pro`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      setStatusMessage("Admin Pro access revoked.");
      await loadUsers(usersPage);
    } catch (err) {
      setErrorMessage(err.message || "Failed to revoke Pro.");
    } finally {
      setActionUserId(null);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    setCreatingCoupon(true);
    setStatusMessage("");
    setErrorMessage("");
    try {
      const body = {
        code: couponForm.code.trim(),
        name: couponForm.name.trim(),
        duration: couponForm.duration,
      };
      if (couponForm.discountType === "percent") {
        body.percentOff = Number(couponForm.percentOff);
      } else {
        body.amountOffDollars = Number(couponForm.amountOffDollars);
      }
      if (couponForm.duration === "repeating") {
        body.durationInMonths = Number(couponForm.durationInMonths);
      }
      if (couponForm.maxRedemptions.trim()) {
        body.maxRedemptions = Number(couponForm.maxRedemptions);
      }
      if (couponForm.expiresAt) {
        body.expiresAt = new Date(couponForm.expiresAt).toISOString();
      }
      await apiFetch("/api/v1/admin/coupons", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setStatusMessage(`Promotion code ${couponForm.code.trim().toUpperCase()} created.`);
      setCouponForm((prev) => ({
        ...prev,
        code: "",
        name: "",
        maxRedemptions: "",
        expiresAt: "",
      }));
      await loadCoupons();
    } catch (err) {
      setErrorMessage(err.message || "Failed to create coupon.");
    } finally {
      setCreatingCoupon(false);
    }
  };

  const handleDeactivateCoupon = async (promoId, code) => {
    if (!window.confirm(`Deactivate promotion code ${code}?`)) return;
    setStatusMessage("");
    setErrorMessage("");
    try {
      await apiFetch(`/api/v1/admin/coupons/${encodeURIComponent(promoId)}/deactivate`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      setStatusMessage(`Promotion code ${code} deactivated.`);
      await loadCoupons();
    } catch (err) {
      setErrorMessage(err.message || "Failed to deactivate coupon.");
    }
  };

  if (!isLoaded || (isSignedIn && isStatusLoading)) {
    return (
      <div className="admin-page">
        <p className="admin-muted">
          <Loader2 className="admin-spin" size={18} aria-hidden="true" /> Loading admin panel…
        </p>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="admin-page admin-page--gate">
        <header className="admin-header">
          <Link to="/" className="admin-back">
            <ArrowLeft size={18} aria-hidden="true" />
            Back to chat
          </Link>
          <p className="admin-eyebrow">Accessibility Assistant</p>
          <h1 className="admin-title">Access admin</h1>
          <p className="admin-subtitle">
            Internal tools for Optimal Solutions Group to manage who has Pro
            access and create Stripe discount codes — without digging through
            Stripe or the database.
          </p>
        </header>

        <ul className="admin-capability-list">
          {ADMIN_CAPABILITIES.map(({ icon: Icon, title, description }) => (
            <li key={title} className="admin-capability">
              <span className="admin-capability-icon" aria-hidden="true">
                <Icon size={20} strokeWidth={2} />
              </span>
              <div>
                <h2 className="admin-capability-title">{title}</h2>
                <p className="admin-capability-desc">{description}</p>
              </div>
            </li>
          ))}
        </ul>

        <div className="admin-card admin-gate-cta" role="status">
          <div className="admin-gate-cta-copy">
            <Shield size={22} aria-hidden="true" />
            <div>
              <h2 className="admin-gate-cta-title">Sign in to continue</h2>
              <p>
                Use your <strong>@optimalsolutionsgroup.com</strong> email.
                Other accounts can use the chat but cannot open this panel.
              </p>
            </div>
          </div>
          <SignInButton mode="modal">
            <button type="button" className="admin-btn admin-btn-primary admin-btn-lg">
              <KeyRound size={16} aria-hidden="true" />
              Sign in with OSG account
            </button>
          </SignInButton>
        </div>
      </div>
    );
  }

  if (!isSuperUser) {
    return (
      <div className="admin-page admin-page--gate">
        <header className="admin-header">
          <Link to="/" className="admin-back">
            <ArrowLeft size={18} aria-hidden="true" />
            Back to chat
          </Link>
          <p className="admin-eyebrow">Accessibility Assistant</p>
          <h1 className="admin-title">Access admin</h1>
          <p className="admin-subtitle">
            This panel is for Optimal Solutions Group staff who manage user
            access and promotion codes.
          </p>
        </header>

        <ul className="admin-capability-list" aria-hidden="true">
          {ADMIN_CAPABILITIES.map(({ icon: Icon, title, description }) => (
            <li key={title} className="admin-capability admin-capability--muted">
              <span className="admin-capability-icon" aria-hidden="true">
                <Icon size={20} strokeWidth={2} />
              </span>
              <div>
                <h2 className="admin-capability-title">{title}</h2>
                <p className="admin-capability-desc">{description}</p>
              </div>
            </li>
          ))}
        </ul>

        <div className="admin-card admin-gate-cta" role="alert">
          <div className="admin-gate-cta-copy">
            <Shield size={22} aria-hidden="true" />
            <div>
              <h2 className="admin-gate-cta-title">Access denied</h2>
              <p>
                Your signed-in email is not an
                {" "}
                <strong>@optimalsolutionsgroup.com</strong>
                {" "}
                account. Switch accounts or contact an OSG admin if you need access.
              </p>
            </div>
          </div>
          <div className="admin-gate-cta-actions">
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              onClick={() => clerk.openUserProfile()}
            >
              Account settings
            </button>
            <Link to="/" className="admin-btn">
              Return to chat
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(usersTotal / 50));

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="admin-header-top">
          <Link to="/" className="admin-back">
            <ArrowLeft size={18} aria-hidden="true" />
            Back to chat
          </Link>
        </div>
        <p className="admin-eyebrow">Accessibility Assistant</p>
        <h1 className="admin-title">Access admin</h1>
        <p className="admin-subtitle">
          Manage users, grant Pro without Stripe, and create Stripe promotion codes.
        </p>
      </header>

      <div className="admin-tabs" role="tablist" aria-label="Admin sections">
        <button
          type="button"
          role="tab"
          id="admin-tab-users"
          aria-selected={tab === "users"}
          aria-controls="admin-panel-users"
          className={`admin-tab${tab === "users" ? " is-active" : ""}`}
          onClick={() => setTab("users")}
        >
          <Users size={16} aria-hidden="true" />
          Users
        </button>
        <button
          type="button"
          role="tab"
          id="admin-tab-coupons"
          aria-selected={tab === "coupons"}
          aria-controls="admin-panel-coupons"
          className={`admin-tab${tab === "coupons" ? " is-active" : ""}`}
          onClick={() => setTab("coupons")}
        >
          <BadgePercent size={16} aria-hidden="true" />
          Coupons
        </button>
      </div>

      <div className="admin-live" aria-live="polite" aria-atomic="true">
        {statusMessage && <p className="admin-status">{statusMessage}</p>}
        {errorMessage && <p className="admin-error" role="alert">{errorMessage}</p>}
      </div>

      {tab === "users" && (
        <section
          id="admin-panel-users"
          role="tabpanel"
          aria-labelledby="admin-tab-users"
          className="admin-section"
        >
          <div className="admin-toolbar">
            <form
              className="admin-search-form"
              onSubmit={(e) => {
                e.preventDefault();
                loadUsers(1, userSearch);
              }}
            >
              <label htmlFor="admin-user-search" className="sr-only">
                Search users
              </label>
              <input
                id="admin-user-search"
                type="search"
                placeholder="Search by email, name, or Clerk ID"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="admin-input"
              />
              <button type="submit" className="admin-btn admin-btn-primary">
                Search
              </button>
            </form>
            <button
              type="button"
              className="admin-btn"
              onClick={() => loadUsers(usersPage)}
              disabled={usersLoading}
              aria-label="Refresh users"
            >
              <RefreshCw size={16} aria-hidden="true" />
              Refresh
            </button>
          </div>

          <p className="admin-muted">{usersTotal} user{usersTotal === 1 ? "" : "s"}</p>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <caption className="sr-only">Registered users and access status</caption>
              <thead>
                <tr>
                  <th scope="col">Email</th>
                  <th scope="col">Name</th>
                  <th scope="col">Access</th>
                  <th scope="col">Subscription</th>
                  <th scope="col">Last login</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersLoading && (
                  <tr>
                    <td colSpan={6}>Loading users…</td>
                  </tr>
                )}
                {!usersLoading && users.length === 0 && (
                  <tr>
                    <td colSpan={6}>No users found.</td>
                  </tr>
                )}
                {!usersLoading &&
                  users.map((user) => (
                    <tr key={user.clerkId}>
                      <td>
                        <div className="admin-email">{user.email || "—"}</div>
                        <div className="admin-muted admin-id">{user.clerkId}</div>
                      </td>
                      <td>{user.displayName || "—"}</td>
                      <td>
                        <span className={`admin-badge access-${user.accessSource}`}>
                          {accessLabel(user.accessSource)}
                        </span>
                        {user.trialExpired && user.accessSource === "trial" && (
                          <span className="admin-muted"> (expired)</span>
                        )}
                      </td>
                      <td>
                        {user.subscription ? (
                          <>
                            {user.subscription.provider}/{user.subscription.status}
                            {user.subscription.currentPeriodEnd && (
                              <div className="admin-muted">
                                Ends {formatDate(user.subscription.currentPeriodEnd)}
                              </div>
                            )}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{formatDate(user.lastLogin || user.createdAt)}</td>
                      <td>
                        <div className="admin-actions">
                          {!user.isSuperUser && (
                            <button
                              type="button"
                              className="admin-btn admin-btn-small admin-btn-primary"
                              onClick={() => handleGrantPro(user.clerkId)}
                              disabled={actionUserId === user.clerkId}
                            >
                              Grant Pro
                            </button>
                          )}
                          {user.canRevokeAdminPro && (
                            <button
                              type="button"
                              className="admin-btn admin-btn-small admin-btn-danger"
                              onClick={() => handleRevokePro(user.clerkId)}
                              disabled={actionUserId === user.clerkId}
                            >
                              Revoke
                            </button>
                          )}
                          {user.isSuperUser && (
                            <span className="admin-muted">Auto Pro</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="admin-pagination">
              <button
                type="button"
                className="admin-btn"
                disabled={usersPage <= 1 || usersLoading}
                onClick={() => loadUsers(usersPage - 1)}
              >
                Previous
              </button>
              <span className="admin-muted">
                Page {usersPage} of {totalPages}
              </span>
              <button
                type="button"
                className="admin-btn"
                disabled={usersPage >= totalPages || usersLoading}
                onClick={() => loadUsers(usersPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </section>
      )}

      {tab === "coupons" && (
        <section
          id="admin-panel-coupons"
          role="tabpanel"
          aria-labelledby="admin-tab-coupons"
          className="admin-section"
        >
          <p className="admin-note">
            Only Accessibility Assistant coupons are listed here (tagged in Stripe).
            Customers enter these codes on Stripe Checkout after clicking Upgrade.
          </p>

          <form className="admin-card admin-form" onSubmit={handleCreateCoupon}>
            <h2 className="admin-section-title">Create promotion code</h2>

            <div className="admin-form-grid">
              <div className="admin-field">
                <label htmlFor="coupon-code">Custom code</label>
                <input
                  id="coupon-code"
                  className="admin-input"
                  required
                  value={couponForm.code}
                  onChange={(e) =>
                    setCouponForm((prev) => ({ ...prev, code: e.target.value }))
                  }
                  placeholder="e.g. SPRING25"
                  autoComplete="off"
                />
              </div>

              <div className="admin-field">
                <label htmlFor="coupon-name">Internal name (optional)</label>
                <input
                  id="coupon-name"
                  className="admin-input"
                  value={couponForm.name}
                  onChange={(e) =>
                    setCouponForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Spring campaign"
                />
              </div>

              <fieldset className="admin-field admin-fieldset">
                <legend>Discount type</legend>
                <label className="admin-radio">
                  <input
                    type="radio"
                    name="discountType"
                    checked={couponForm.discountType === "percent"}
                    onChange={() =>
                      setCouponForm((prev) => ({ ...prev, discountType: "percent" }))
                    }
                  />
                  Percent off
                </label>
                <label className="admin-radio">
                  <input
                    type="radio"
                    name="discountType"
                    checked={couponForm.discountType === "amount"}
                    onChange={() =>
                      setCouponForm((prev) => ({ ...prev, discountType: "amount" }))
                    }
                  />
                  Fixed amount off (USD)
                </label>
              </fieldset>

              {couponForm.discountType === "percent" ? (
                <div className="admin-field">
                  <label htmlFor="coupon-percent">Percent off</label>
                  <input
                    id="coupon-percent"
                    className="admin-input"
                    type="number"
                    min="0.01"
                    max="100"
                    step="0.01"
                    required
                    value={couponForm.percentOff}
                    onChange={(e) =>
                      setCouponForm((prev) => ({ ...prev, percentOff: e.target.value }))
                    }
                  />
                </div>
              ) : (
                <div className="admin-field">
                  <label htmlFor="coupon-amount">Amount off (USD)</label>
                  <input
                    id="coupon-amount"
                    className="admin-input"
                    type="number"
                    min="0.01"
                    step="0.01"
                    required
                    value={couponForm.amountOffDollars}
                    onChange={(e) =>
                      setCouponForm((prev) => ({
                        ...prev,
                        amountOffDollars: e.target.value,
                      }))
                    }
                  />
                </div>
              )}

              <div className="admin-field">
                <label htmlFor="coupon-duration">Duration</label>
                <select
                  id="coupon-duration"
                  className="admin-input"
                  value={couponForm.duration}
                  onChange={(e) =>
                    setCouponForm((prev) => ({ ...prev, duration: e.target.value }))
                  }
                >
                  <option value="once">Once</option>
                  <option value="repeating">Repeating</option>
                  <option value="forever">Forever</option>
                </select>
              </div>

              {couponForm.duration === "repeating" && (
                <div className="admin-field">
                  <label htmlFor="coupon-months">Duration in months</label>
                  <input
                    id="coupon-months"
                    className="admin-input"
                    type="number"
                    min="1"
                    required
                    value={couponForm.durationInMonths}
                    onChange={(e) =>
                      setCouponForm((prev) => ({
                        ...prev,
                        durationInMonths: e.target.value,
                      }))
                    }
                  />
                </div>
              )}

              <div className="admin-field">
                <label htmlFor="coupon-max">Max redemptions (optional)</label>
                <input
                  id="coupon-max"
                  className="admin-input"
                  type="number"
                  min="1"
                  value={couponForm.maxRedemptions}
                  onChange={(e) =>
                    setCouponForm((prev) => ({
                      ...prev,
                      maxRedemptions: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="admin-field">
                <label htmlFor="coupon-expires">Expires (optional)</label>
                <input
                  id="coupon-expires"
                  className="admin-input"
                  type="datetime-local"
                  value={couponForm.expiresAt}
                  onChange={(e) =>
                    setCouponForm((prev) => ({ ...prev, expiresAt: e.target.value }))
                  }
                />
              </div>
            </div>

            <button
              type="submit"
              className="admin-btn admin-btn-primary"
              disabled={creatingCoupon}
            >
              {creatingCoupon ? "Creating…" : "Create coupon"}
            </button>
          </form>

          <div className="admin-toolbar">
            <h2 className="admin-section-title">Existing codes</h2>
            <button
              type="button"
              className="admin-btn"
              onClick={loadCoupons}
              disabled={couponsLoading}
              aria-label="Refresh coupons"
            >
              <RefreshCw size={16} aria-hidden="true" />
              Refresh
            </button>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <caption className="sr-only">Stripe promotion codes</caption>
              <thead>
                <tr>
                  <th scope="col">Code</th>
                  <th scope="col">Discount</th>
                  <th scope="col">Duration</th>
                  <th scope="col">Redemptions</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {couponsLoading && (
                  <tr>
                    <td colSpan={6}>Loading coupons…</td>
                  </tr>
                )}
                {!couponsLoading && coupons.length === 0 && (
                  <tr>
                    <td colSpan={6}>No promotion codes yet.</td>
                  </tr>
                )}
                {!couponsLoading &&
                  coupons.map((coupon) => (
                    <tr key={coupon.id}>
                      <td>
                        <strong>{coupon.code}</strong>
                        {coupon.name && (
                          <div className="admin-muted">{coupon.name}</div>
                        )}
                      </td>
                      <td>{discountLabel(coupon)}</td>
                      <td>
                        {coupon.duration}
                        {coupon.duration === "repeating" && coupon.durationInMonths
                          ? ` (${coupon.durationInMonths} mo)`
                          : ""}
                      </td>
                      <td>
                        {coupon.timesRedeemed}
                        {coupon.maxRedemptions != null
                          ? ` / ${coupon.maxRedemptions}`
                          : ""}
                      </td>
                      <td>
                        <span
                          className={`admin-badge ${
                            coupon.active ? "access-stripe" : "access-trial"
                          }`}
                        >
                          {coupon.active ? "Active" : "Inactive"}
                        </span>
                        {coupon.expiresAt && (
                          <div className="admin-muted">
                            Expires {formatDate(coupon.expiresAt * 1000)}
                          </div>
                        )}
                      </td>
                      <td>
                        {coupon.active && (
                          <button
                            type="button"
                            className="admin-btn admin-btn-small admin-btn-danger"
                            onClick={() =>
                              handleDeactivateCoupon(coupon.id, coupon.code)
                            }
                          >
                            Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
